/**
 * UnventedCylinderDiagram — Animated SVG showing unvented cylinder requirements.
 * Shows mains-pressurised stored DHW with safety components (G3).
 */

export function UnventedCylinderDiagram() {
  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox="0 0 480 300" width="100%" role="img" aria-label="Unvented cylinder requirements and safety components">
        <title>Unvented cylinder: mains-pressurised stored hot water with G3 safety</title>
        <defs>
          <style>{`
            .ucd-pipe { stroke: #64748b; stroke-width: 2.5; fill: none; }
            .ucd-flow-cold { stroke: #3b82f6; stroke-width: 2; fill: none; opacity: 0.5; stroke-dasharray: 6,5; animation: ucd-dash 1.2s linear infinite; }
            .ucd-flow-hot { stroke: #ef4444; stroke-width: 2; fill: none; opacity: 0.5; stroke-dasharray: 6,5; animation: ucd-dash 1s linear infinite; }
            .ucd-label { font-family: system-ui, sans-serif; font-size: 11px; fill: #475569; text-anchor: middle; }
            .ucd-heading { font-family: system-ui, sans-serif; font-size: 13px; fill: #1e293b; font-weight: 600; text-anchor: middle; }
            .ucd-component { fill: #f1f5f9; stroke: #475569; stroke-width: 1.5; }
            .ucd-safety { fill: #fef3c7; stroke: #f59e0b; stroke-width: 1.5; }
            .ucd-req { font-family: system-ui, sans-serif; font-size: 10px; fill: #1e293b; }
            @keyframes ucd-dash { to { stroke-dashoffset: -11; } }
          `}</style>
        </defs>

        <text x="240" y="22" className="ucd-heading">Unvented Cylinder — Mains-Pressure Hot Water</text>

        {/* Mains cold supply */}
        <rect x="20" y="80" width="55" height="30" rx="4" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1.5" />
        <text x="47" y="99" className="ucd-label" style={{ fontSize: '10px' }}>Cold mains</text>

        {/* Cold pipe to cylinder */}
        <line x1="75" y1="95" x2="140" y2="95" className="ucd-pipe" />
        <line x1="75" y1="95" x2="140" y2="95" className="ucd-flow-cold" />

        {/* Pressure reducing valve */}
        <rect x="100" y="80" width="40" height="16" rx="3" className="ucd-safety" />
        <text x="120" y="92" className="ucd-label" style={{ fontSize: '8px' }}>PRV</text>

        {/* Expansion vessel */}
        <ellipse cx="120" cy="140" rx="20" ry="14" className="ucd-safety" />
        <text x="120" y="144" className="ucd-label" style={{ fontSize: '8px' }}>EV</text>
        <line x1="120" y1="110" x2="120" y2="126" className="ucd-pipe" />

        {/* Cylinder */}
        <rect x="155" y="60" width="70" height="100" rx="10" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
        <text x="190" y="100" className="ucd-label" style={{ fontWeight: 600 }}>Unvented</text>
        <text x="190" y="115" className="ucd-label" style={{ fontSize: '10px' }}>Cylinder</text>

        {/* Temperature layers inside cylinder */}
        <rect x="165" y="70" width="50" height="25" rx="3" fill="#fca5a5" opacity="0.4" />
        <text x="190" y="86" className="ucd-label" style={{ fontSize: '8px', fill: '#b91c1c' }}>Hot</text>
        <rect x="165" y="120" width="50" height="25" rx="3" fill="#93c5fd" opacity="0.4" />
        <text x="190" y="138" className="ucd-label" style={{ fontSize: '8px', fill: '#1d4ed8' }}>Cold</text>

        {/* T&P valve */}
        <rect x="230" y="70" width="35" height="16" rx="3" className="ucd-safety" />
        <text x="247" y="81" className="ucd-label" style={{ fontSize: '7px' }}>T&amp;P</text>

        {/* Tundish */}
        <line x1="265" y1="78" x2="290" y2="78" className="ucd-pipe" />
        <rect x="290" y="70" width="25" height="16" rx="3" className="ucd-safety" />
        <text x="302" y="81" className="ucd-label" style={{ fontSize: '7px' }}>Tun</text>

        {/* Discharge pipe */}
        <line x1="315" y1="78" x2="340" y2="78" className="ucd-pipe" />
        <line x1="340" y1="78" x2="340" y2="110" className="ucd-pipe" />
        <text x="355" y="98" className="ucd-label" style={{ fontSize: '8px' }}>Drain</text>

        {/* Hot output to taps */}
        <line x1="225" y1="90" x2="280" y2="90" className="ucd-pipe" />
        <line x1="280" y1="90" x2="280" y2="130" className="ucd-pipe" />
        <line x1="225" y1="90" x2="280" y2="130" className="ucd-flow-hot" />

        {/* Multiple outlets */}
        <rect x="260" y="130" width="40" height="22" rx="4" fill="#dcfce7" stroke="#16a34a" strokeWidth="1.5" />
        <text x="280" y="145" className="ucd-label" style={{ fontSize: '9px' }}>🚿 🚿</text>
        <text x="280" y="167" className="ucd-label" style={{ fontSize: '9px', fill: '#16a34a' }}>Mains pressure</text>

        {/* Boiler heating coil */}
        <rect x="155" y="175" width="70" height="30" rx="6" fill="#f1f5f9" stroke="#475569" strokeWidth="1.5" />
        <text x="190" y="194" className="ucd-label" style={{ fontSize: '10px' }}>Boiler feed</text>
        <line x1="190" y1="160" x2="190" y2="175" className="ucd-pipe" />
        <line x1="190" y1="160" x2="190" y2="175" className="ucd-flow-hot" />

        {/* Requirements box */}
        <rect x="20" y="215" width="440" height="75" rx="8" fill="#f0fdf4" stroke="#86efac" strokeWidth="1" />
        <text x="240" y="235" className="ucd-heading" style={{ fontSize: '12px', fill: '#166534' }}>Requirements</text>

        <text x="35" y="252" className="ucd-req">
          <tspan fontWeight="600">Min pressure:</tspan> 1.5 bar dynamic · <tspan fontWeight="600">Recommended:</tspan> 2+ bar
        </text>
        <text x="35" y="268" className="ucd-req">
          <tspan fontWeight="600">Min flow:</tspan> 15 L/min · <tspan fontWeight="600">Recommended:</tspan> 20+ L/min
        </text>
        <text x="35" y="284" className="ucd-req">
          <tspan fontWeight="600">Certification:</tspan> G3 qualified installer required (Building Regulations)
        </text>
      </svg>
    </figure>
  );
}
